import { useState } from "react";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import ExpandPhoto from "components/expand-photo";
import FilePicker from "components/file-picker";
import PhotoPicker from "components/photo-picker";
import { useUpdateProductPictureMutation } from "services/products";

// Matches the backend's PRODUCT_PICTURE_MAX_SIZE (PNG/JPG only, ≤500KB).
export const PRODUCT_PICTURE_MAX_SIZE_BYTES = 500 * 1024;

type ProductPictureSectionProps = {
  productId: number;
  pictureUrl?: string | null;
  isEditable: boolean;
};

// Upload/view tile for the product's device picture. Saves immediately via a
// dedicated multipart PATCH (like the patient avatar), independent of the
// surrounding product form submit.
export default function ProductPictureSection({
  productId,
  pictureUrl,
  isEditable
}: ProductPictureSectionProps) {
  const dispatch = useAppDispatch();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expandOpen, setExpandOpen] = useState(false);
  const [updateProductPicture, { isLoading }] =
    useUpdateProductPictureMutation();

  const handleSubmit = async (files: File[]) => {
    const formData = new FormData();
    if (files?.length) {
      files.forEach((file) => formData.append("picture", file));
    } else {
      // Empty string clears the picture on the backend.
      formData.append("picture", "");
    }

    const response = await updateProductPicture({ id: productId, formData });
    if ("error" in response) {
      dispatch(setCustomMessage(<ErrorSnackbarContent response={response} />));
      dispatch(setOpen(true));
    } else {
      setPickerOpen(false);
    }
  };

  return (
    <Grid item container xs={12} sx={{ mb: 5 }}>
      <Stack alignItems="flex-start" gap={0.5}>
        <Typography sx={{ fontWeight: "fontWeightBold" }}>
          Device Picture
        </Typography>
        <FilePicker
          imgSrc={pictureUrl ?? ""}
          onExpandPhoto={pictureUrl ? () => setExpandOpen(true) : undefined}
          onChangePhoto={isEditable ? () => setPickerOpen(true) : undefined}
        />
        <Typography variant="caption" color="text.secondary">
          PNG or JPG, up to 500KB
        </Typography>
      </Stack>

      <PhotoPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        hasInitialImage={Boolean(pictureUrl)}
        maxSizeLimit={PRODUCT_PICTURE_MAX_SIZE_BYTES}
      />

      <ExpandPhoto
        open={expandOpen}
        onClose={() => setExpandOpen(false)}
        imgSrc={pictureUrl ?? ""}
      />
    </Grid>
  );
}
